import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, CreditCard, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TIERS = [
  {
    name: "Gratuit",
    tier: "FREE",
    price: "0 $",
    suffix: "/ mois",
    lookup_key: null,
    desc: "Découvrir la plateforme",
    features: ["15 recherches / mois", "IA de base", "1 utilisateur", "Support courriel"],
  },
  {
    name: "Pro",
    tier: "PRO",
    price: "89 $",
    suffix: "/ mois CAD",
    lookup_key: "cca_pro_monthly",
    featured: true,
    desc: "Entrepreneurs indépendants",
    features: [
      "Recherches illimitées",
      "Génération IA complète",
      "CRM + relances",
      "Veille SEAO quotidienne",
      "Alertes courriel",
      "Analyse PDF de profil",
    ],
  },
  {
    name: "Business",
    tier: "BUSINESS",
    price: "249 $",
    suffix: "/ mois CAD",
    lookup_key: "cca_business_monthly",
    desc: "Équipes commerciales",
    features: [
      "Tout Pro inclus",
      "5 utilisateurs",
      "Analytics avancées",
      "Priorité IA",
      "Support prioritaire",
      "Zones étendues",
    ],
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);
  const currentTier = user?.subscription_tier || "FREE";

  const subscribe = async (lookup_key) => {
    if (!lookup_key) return;
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de paiement");
      setLoading(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="pricing-page">
      <div className="text-center">
        <div className="text-xs uppercase tracking-wider text-cyan-400 font-mono mb-2">Abonnement</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-3">
          Choisissez votre forfait
        </h1>
        <p className="text-muted-foreground max-w-xl mx-auto">
          Passez au niveau supérieur pour débloquer la génération IA illimitée, les alertes courriel et le CRM complet.
        </p>
        <Badge variant="outline" className="mt-4 border-border">
          Plan actuel : <span className="text-cyan-300 ml-1 font-semibold">{currentTier}</span>
        </Badge>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {TIERS.map((t) => {
          const isCurrent = t.tier === currentTier;
          return (
            <Card
              key={t.tier}
              data-testid={`tier-card-${t.tier.toLowerCase()}`}
              className={`p-8 relative ${t.featured ? "bg-gradient-to-b from-cyan-500/10 to-transparent border-cyan-500/40" : "bg-card border-border"}`}
            >
              {t.featured && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cyan-500 text-slate-950">Populaire</Badge>
              )}
              <div className="text-sm font-medium text-muted-foreground mb-2">{t.name}</div>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="font-display text-4xl font-bold">{t.price}</span>
                {t.suffix && <span className="text-sm text-muted-foreground">{t.suffix}</span>}
              </div>
              <p className="text-sm text-muted-foreground mb-6">{t.desc}</p>
              <ul className="space-y-2 mb-6">
                {t.features.map((f, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              {isCurrent ? (
                <Button className="w-full" variant="outline" disabled data-testid={`current-plan-${t.tier.toLowerCase()}`}>
                  <Sparkles className="h-4 w-4 mr-2" /> Plan actuel
                </Button>
              ) : t.lookup_key ? (
                <Button
                  className={`w-full ${t.featured ? "bg-cyan-500 hover:bg-cyan-400 text-slate-950" : ""}`}
                  variant={t.featured ? "default" : "outline"}
                  onClick={() => subscribe(t.lookup_key)}
                  disabled={loading === t.lookup_key}
                  data-testid={`subscribe-button-${t.tier.toLowerCase()}`}
                >
                  {loading === t.lookup_key ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                  {loading === t.lookup_key ? "Redirection…" : `Passer à ${t.name}`}
                </Button>
              ) : (
                <Button className="w-full" variant="outline" disabled>Plan de base</Button>
              )}
            </Card>
          );
        })}
      </div>

      <div className="text-center text-xs text-muted-foreground max-w-lg mx-auto space-y-2">
        <p>Paiement sécurisé par Stripe. Mode test actif — utilisez la carte <code className="font-mono bg-secondary/50 px-1.5 py-0.5 rounded">4242 4242 4242 4242</code>, date future, CVC libre.</p>
        <p>Annulation possible à tout moment depuis votre compte Stripe.</p>
      </div>
    </div>
  );
}
